import { withNamespaces } from "react-i18next";
import { Alert, Button, Col, Label, Row, Spinner } from "reactstrap";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { connect } from "react-redux";

import ConfluenceSettingsService, {
    getConfluenceApiError,
} from "src/modules/3rd-party-management/apis/admin/ConfluenceSettingsService";
import ToggleActivationModal from "./components/ToggleActivationModal";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import { INTERNATIONAL_DATE_TIME_FORMAT } from "src/common/constants";
import DateUtils from "src/services/utils/DateUtils";
import UserUtils from "src/services/utils/UserUtils";

const ConfluenceSettings = ({
    t,
    user,
}) => {
    const formRef = useRef();
    const [initialSettings, setInitialSettings] = useState();
    const [defaultSettings, setDefaultSettings] = useState({});
    const [formVersion, setFormVersion] = useState(0);
    const [testSuccess, setTestSuccess] = useState(null);
    const [active, setActive] = useState(false);
    const [showToggleActivationModal, setShowToggleActivationModal] = useState(false);
    const [featureInactiveMessage, setFeatureInactiveMessage] = useState("");
    const service = ConfluenceSettingsService.getInstance();
    const dateUtils = useMemo(() => new DateUtils(), []);
    const canManage = useMemo(() => {
        const userUtils = new UserUtils();

        return userUtils.isAnalystAdmin(user) || userUtils.isPartner(user);
    }, [user]);

    const applySettings = (data, { remountForm = false } = {}) => {
        const nextSettings = {
            ...(data || {}),
            apiToken: "",
        };

        setInitialSettings(data || {});
        setDefaultSettings(nextSettings);
        setActive(Boolean(data?.isActive));

        if (remountForm) {
            setFormVersion((version) => version + 1);
        }
    };

    const showRequestError = (error, fallback) => {
        const apiError = getConfluenceApiError(error);

        if (apiError.status === 403 || apiError.code === "feature_inactive") {
            setFeatureInactiveMessage(
                apiError.message || t("This section is not active in feature flagging.")
            );
            return apiError;
        }

        toast(apiError.message || t(fallback), {
            type: "error",
        });

        return apiError;
    };

    const {
        refetch,
        isFetching: isFetchingSettings
    } = useQuery({
        queryKey: ["3rd-party-management-fetch-confluence-settings"],
        queryFn: async () => {
            return await service.fetchSettings();
        },
        enabled: canManage && isFeatureEnabledForActiveModule("confluence_setting"),
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
            applySettings(data);
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            showRequestError(error, "An error occurred while fetching settings.");
        },
    });

    const refreshSavedSettings = async () => {
        const result = await refetch();

        applySettings(result.data, { remountForm: true });
    };

    const submitMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.saveSettings(payload);
        },
        onSuccess: async () => {
            toast.success(t("Settings saved successfully."));
            const result = await refetch();

            if (result.data) {
                applySettings(result.data, { remountForm: true });

                if (!result.data.isActive) {
                    setShowToggleActivationModal("activation_after_save");
                }

                return;
            }

            setDefaultSettings((currentSettings) => ({
                ...(currentSettings || {}),
                apiToken: "",
            }));
            setFormVersion((version) => version + 1);
        },
        onError: async (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            const apiError = showRequestError(error, "An error occurred while saving settings.");

            if (apiError.savedButInvalid) {
                await refreshSavedSettings();
            }
        },
    });

    const toggleActivationMutation = useMutation({
        mutationFn: async () => {
            return await service.toggleActivation();
        },
        onSuccess: (response) => {
            const nextActive = response?.data?.isActive;

            toast.success(t(`Settings ${nextActive ? "activated" : "deactivated"} successfully.`));
            setActive(Boolean(nextActive));
            setInitialSettings((currentSettings) => ({
                ...(currentSettings || {}),
                isActive: Boolean(nextActive),
            }));
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            showRequestError(error, "An error occurred while toggling activation.");
        },
    });

    const testConnectionMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.testConnection(payload);
        },
        onSuccess: () => {
            toast.success(t("Confluence connection test successful."));
            setTestSuccess(true);
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            showRequestError(
                error,
                "Confluence connection test failed. Please check your configuration and try again."
            );
            setTestSuccess(false);
        }
    });

    if (!isFeatureEnabledForActiveModule("confluence_setting")) {
        return (
            <Alert color="warning">
                {t("Please upgrade your plan to access this feature")}.
            </Alert>
        );
    }

    if (!canManage || featureInactiveMessage) {
        return (
            <Alert color="warning">
                {featureInactiveMessage || t("You are not allowed to manage Confluence settings.")}
            </Alert>
        );
    }

    const handleSubmit = (_event, values) => {
        submitMutation.mutate(values);
    };

    const handleTestConnection = () => {
        setTestSuccess(null);
        testConnectionMutation.mutate(formRef.current.getValues());
    };

    const handleRestore = () => {
        applySettings(initialSettings, { remountForm: true });
        setTestSuccess(null);
    };

    const isFirstTime = !initialSettings?.baseUrl
        && !initialSettings?.email
        && !initialSettings?.spaceKey;
    const lastVerifiedAt = initialSettings?.lastVerifiedAt;
    const lastError = initialSettings?.lastError;

    return (
        <Row>
            <Col sm="12" md="12" xl="9">
                {!isFirstTime && (
                    <Alert
                        color={active ? "success" : "warning"}
                        className="mb-4"
                    >
                        {active ? (
                            <>
                                <i className="ri-checkbox-circle-fill me-1 font-size-22 align-middle" />
                                {t("Confluence integration is active. The system is synchronizing with your Confluence workspace.")}
                            </>
                        ) : (
                            <>
                                <i className="ri-error-warning-fill me-1 font-size-22 align-middle" />
                                {t("Confluence integration is currently disabled. Activate it to enable synchronization with your Confluence workspace.")}
                            </>
                        )}
                    </Alert>
                )}

                {(lastVerifiedAt || lastError) && (
                    <Row className="mb-3">
                        {lastVerifiedAt && (
                            <Col sm="12" md="6">
                                <p>
                                    <strong>{t("Last Verified")}:</strong><br />
                                    {dateUtils.convertDateToDate(
                                        lastVerifiedAt,
                                        INTERNATIONAL_DATE_TIME_FORMAT
                                    )}
                                </p>
                            </Col>
                        )}
                        {lastError && (
                            <Col sm="12" md="6">
                                <p className="text-danger mb-0">
                                    <strong>{t("Last Error")}:</strong><br />
                                    {lastError}
                                </p>
                            </Col>
                        )}
                    </Row>
                )}

                <AvForm
                    key={formVersion}
                    className="needs-validation"
                    onValidSubmit={handleSubmit}
                    ref={formRef}
                >
                    <Row>
                        <Col sm="12" md="6" className="mb-3">
                            <Label for="baseUrl">
                                {t("Confluence Base URL")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: "100%" }}
                                />
                            ) : (
                                <AvField
                                    id="baseUrl"
                                    name="baseUrl"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true },
                                        pattern: {
                                            value: /^https:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                                            errorMessage: t("Base URL must use https."),
                                        }
                                    }}
                                    value={defaultSettings?.baseUrl || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="email">
                                {t("Atlassian Account Email")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: "100%" }}
                                />
                            ) : (
                                <AvField
                                    id="email"
                                    name="email"
                                    type="email"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: {
                                            value: true,
                                            errorMessage: t("This field cannot be blank"),
                                        },
                                        email: { errorMessage: t("Your email is invalid") },
                                    }}
                                    value={defaultSettings?.email || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="apiToken">
                                {t("API Token")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: "100%" }}
                                />
                            ) : (
                                <AvField
                                    id="apiToken"
                                    name="apiToken"
                                    type="password"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={defaultSettings?.apiToken || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="spaceKey">
                                {t("Confluence Space Key")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: "100%" }}
                                />
                            ) : (
                                <AvField
                                    id="spaceKey"
                                    name="spaceKey"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={defaultSettings?.spaceKey || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" className="text-end">
                            <div className="d-inline-block">
                                <div>
                                    <Button
                                        color="primary"
                                        type="submit"
                                        disabled={isFetchingSettings || submitMutation.isLoading}
                                        className="me-2"
                                    >
                                        {submitMutation.isLoading && (
                                            <Spinner
                                                size="sm"
                                                color="primary"
                                                className="me-2"
                                            />
                                        )}
                                        {t("Save Settings")}
                                    </Button>

                                    {!isFirstTime && (
                                        <>
                                            <Button
                                                type="button"
                                                color="info"
                                                className="me-2"
                                                onClick={handleTestConnection}
                                                disabled={isFetchingSettings || submitMutation.isLoading || testConnectionMutation.isLoading}
                                            >
                                                {testConnectionMutation.isLoading && (
                                                    <Spinner
                                                        size="sm"
                                                        color="white"
                                                        className="me-2"
                                                    />
                                                )}
                                                {t("Test Connection")}
                                            </Button>

                                            <Button
                                                type="button"
                                                color={active ? "danger" : "success"}
                                                className="me-2"
                                                onClick={() => setShowToggleActivationModal(active ? "deactivate" : "activate")}
                                                disabled={isFetchingSettings || submitMutation.isLoading}
                                            >
                                                {active ? (
                                                    t("Deactivate Confluence Integration")
                                                ) : (
                                                    t("Activate Confluence Integration")
                                                )}
                                            </Button>
                                        </>
                                    )}

                                    <Button
                                        color="secondary"
                                        type="button"
                                        onClick={handleRestore}
                                        disabled={isFetchingSettings || submitMutation.isLoading}
                                    >
                                        {t("Restore Defaults")}
                                    </Button>
                                </div>
                                <div className="text-start mt-1">
                                    {testSuccess === true && (
                                        <span className="text-success">
                                            <i className="ri-check-line align-middle font-size-18" />
                                            {t("Connection successful. Your Confluence settings are valid.")}
                                        </span>
                                    )}
                                    {testSuccess === false && (
                                        <span className="text-danger">
                                            <i className="ri-error-warning-line align-middle font-size-18" />
                                            {t("Connection failed: invalid credentials.")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </AvForm>

                <ToggleActivationModal
                    isOpen={showToggleActivationModal !== false}
                    toggle={() => {
                        setShowToggleActivationModal(false);
                    }}
                    toggleActivationMutation={toggleActivationMutation}
                    action={showToggleActivationModal}
                />
            </Col>
        </Row>
    );
};

const mapStateToProps = (state) => ({
    user: state.Login.user,
});

export default withNamespaces()(
    connect(mapStateToProps)(ConfluenceSettings)
);
